import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format, differenceInDays } from "date-fns";
import { es } from "date-fns/locale";
import { useReactToPrint } from "react-to-print";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Printer, X } from "lucide-react";
import api from "@/lib/api";
import { money, conIVA, fechaMX } from "@/lib/format";
import { PrintHeader, PrintFooter } from "@/components/PrintHeader";

export default function EstadoCuenta() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [facturas, setFacturas] = useState([]);
  const [vendedor, setVendedor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { data: clienteData } = await api.get(`/clientes/${id}`);
        setCliente(clienteData);
        if (clienteData.vendedor_asignado_id) {
          const { data: users } = await api.get("/users");
          const v = users.find((u) => u.id === clienteData.vendedor_asignado_id);
          if (v) setVendedor(v);
        }
        const { data: pedidos } = await api.get(`/pedidos?cliente_id=${id}`);
        const pendientes = (pedidos || []).filter((p) => {
          const facturado = !!(p.factura || p.fecha_facturacion || p.estatus_pago === "Pendiente de cobro" || p.estatus === "Facturación" || p.estatus === "Vencimiento" || p.estatus === "Facturado");
          const noPagado = !(p.estatus_pago === "Pagado" || p.estatus_pago === "Cobrado" || p.fecha_pago || p.estatus === "Pago" || p.estatus === "Pedido cerrado");
          const conSaldo = Number(p.subtotal || 0) > 0;
          return facturado && noPagado && conSaldo;
        });
        setFacturas(pendientes);
      } catch (err) {
        setError(err?.response?.status === 404 ? "Cliente no encontrado" : "Ocurrió un error al cargar los datos");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handlePrint = useReactToPrint({ contentRef: printRef, documentTitle: `EstadoCuenta_${cliente?.id_cliente || ""}` });
  const handleClose = () => navigate(-1);

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow text-center">
          <p className="text-red-500 font-semibold text-lg mb-4">{error}</p>
          <Button onClick={handleClose}>Cerrar</Button>
        </div>
      </div>
    );
  }
  if (loading || !cliente) {
    return <div className="flex h-screen items-center justify-center"><p className="text-muted-foreground animate-pulse">Generando estado de cuenta...</p></div>;
  }

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const saldoTotal = facturas.reduce((a, c) => a + conIVA(c.subtotal), 0);
  let totalVencido = 0, totalPorVencer = 0;
  const procesadas = facturas.map((f) => {
    let diasVencidos = 0;
    if (f.fecha_vencimiento) diasVencidos = differenceInDays(today, new Date(f.fecha_vencimiento + "T00:00:00"));
    const saldo = conIVA(f.subtotal);
    if (diasVencidos > 0) totalVencido += saldo; else totalPorVencer += saldo;
    return { ...f, diasVencidos, saldo };
  });

  return (
    <div className="bg-slate-50 min-h-screen p-4 md:p-8 text-sm doc-scroll doc-actions-safe">
      <div className="doc-sheet max-w-5xl mx-auto flex justify-end gap-3 mb-6 print:hidden">
        <Button onClick={handlePrint} variant="outline" className="bg-white" data-testid="estado-print-btn"><Printer className="mr-2 h-4 w-4" /> Imprimir</Button>
        <Button onClick={handleClose} variant="secondary"><X className="mr-2 h-4 w-4" /> Cerrar</Button>
      </div>

      <div ref={printRef} className="doc-sheet max-w-5xl mx-auto bg-white border p-10 md:p-12 shadow-sm rounded-lg" data-testid="estado-cuenta-doc">
        <PrintHeader title="Estado de Cuenta" folio={cliente.id_cliente} fecha={fechaMX(new Date())} screen />
        <div className="flex flex-col md:flex-row justify-between items-start mb-10 border-b pb-6">
          <div className="mb-4 md:mb-0">
            <h2 className="text-xl font-bold text-slate-800">{cliente.nombre_razon_social}</h2>
            <div className="mt-2 text-slate-600 space-y-1 text-xs md:text-sm">
              <p><strong>ID Cliente:</strong> <span className="font-mono">{cliente.id_cliente || "-"}</span></p>
              <p><strong>RFC:</strong> {cliente.rfc || "-"}</p>
              <p><strong>Contacto:</strong> {cliente.nombre_contacto || "-"}</p>
            </div>
          </div>
          <div className="text-left md:text-right text-xs md:text-sm">
            <p className="font-semibold text-slate-900 mb-1">Fecha de Emisión</p>
            <p className="text-slate-600 mb-4">{format(new Date(), "dd 'de' MMMM, yyyy", { locale: es })}</p>
            <p className="font-semibold text-slate-900 mb-1">Vendedor Asignado</p>
            <p className="text-slate-600">{vendedor?.full_name || vendedor?.email || "-"}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12 items-stretch">
          <Card className="bg-slate-50 border-slate-100 shadow-none"><CardContent className="p-5"><p className="text-slate-500 text-xs font-medium mb-1 uppercase tracking-wider">Facturas Pendientes</p><p className="text-2xl font-bold text-slate-900">{facturas.length}</p></CardContent></Card>
          <Card className="bg-slate-50 border-slate-100 shadow-none"><CardContent className="p-5"><p className="text-slate-500 text-xs font-medium mb-1 uppercase tracking-wider">Total Pendiente</p><p className="text-2xl font-bold text-slate-900">{money(saldoTotal)}</p></CardContent></Card>
          <Card className="bg-slate-50 border-slate-100 shadow-none"><CardContent className="p-5"><p className="text-slate-500 text-xs font-medium mb-1 uppercase tracking-wider">Total por Vencer</p><p className="text-2xl font-bold text-green-600">{money(totalPorVencer)}</p></CardContent></Card>
          <Card className="bg-white border-red-200 shadow-md"><CardContent className="p-5"><p className="text-red-500 font-semibold text-xs mb-1 uppercase tracking-wider">Total Vencido</p><p className="text-3xl font-black text-red-600 tracking-tight">{money(totalVencido)}</p></CardContent></Card>
        </div>

        <div className="mb-12">
          <h3 className="text-lg font-bold mb-4 text-slate-800 border-b pb-2">Detalle de Facturas Pendientes</h3>
          {procesadas.length === 0 ? (
            <p className="text-slate-500 italic py-4 text-center bg-slate-50 rounded">No hay facturas pendientes de pago.</p>
          ) : (
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader className="bg-slate-100">
                  <TableRow>
                    <TableHead>Factura</TableHead><TableHead>Fecha Factura</TableHead><TableHead>Fecha Vencimiento</TableHead>
                    <TableHead className="text-center">Días Vencidos</TableHead><TableHead className="text-center">Estado</TableHead><TableHead className="text-right">Saldo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {procesadas.map((f) => {
                    let bg = "bg-green-100", txt = "text-green-800", label = "Al corriente";
                    if (f.diasVencidos > 0) { bg = "bg-red-100"; txt = "text-red-800"; label = "Vencido"; }
                    else if (f.diasVencidos >= -7 && f.diasVencidos <= 0) { bg = "bg-yellow-100"; txt = "text-yellow-800"; label = "Por vencer"; }
                    return (
                      <TableRow key={f.id}>
                        <TableCell className="font-medium">{f.factura}</TableCell>
                        <TableCell>{fechaMX(f.fecha_facturacion)}</TableCell>
                        <TableCell>{fechaMX(f.fecha_vencimiento)}</TableCell>
                        <TableCell className="text-center">{f.diasVencidos > 0 ? <span className="text-red-600 font-bold">{f.diasVencidos}</span> : <span className="text-slate-400">-</span>}</TableCell>
                        <TableCell className="text-center"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bg} ${txt}`}>{label}</span></TableCell>
                        <TableCell className="text-right font-bold text-slate-800">{money(f.saldo)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                <TableFooter className="bg-slate-50 font-bold text-slate-900">
                  <TableRow><TableCell colSpan={5} className="text-right">Total Pendiente:</TableCell><TableCell className="text-right">{money(saldoTotal)}</TableCell></TableRow>
                </TableFooter>
              </Table>
            </div>
          )}
        </div>

        {procesadas.filter((f) => f.diasVencidos > 0).length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-bold mb-4 text-slate-800 border-b pb-2">Antigüedad de Saldos</h3>
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader className="bg-slate-100"><TableRow><TableHead>Factura</TableHead><TableHead className="text-center">Retraso</TableHead><TableHead className="text-right">Saldo</TableHead></TableRow></TableHeader>
                <TableBody>
                  {procesadas.filter((f) => f.diasVencidos > 0).map((f) => (
                    <TableRow key={f.id}>
                      <TableCell className="font-medium text-slate-700">{f.factura}</TableCell>
                      <TableCell className="text-center font-bold text-red-600">{f.diasVencidos} DÍAS DE RETRASO</TableCell>
                      <TableCell className="text-right font-medium text-slate-800">{money(f.saldo)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter className="bg-slate-50 font-bold text-slate-900"><TableRow><TableCell colSpan={2} className="text-right">Total Vencido:</TableCell><TableCell className="text-right">{money(totalVencido)}</TableCell></TableRow></TableFooter>
              </Table>
            </div>
          </div>
        )}
        <PrintFooter screen />
      </div>
    </div>
  );
}
